import { useEffect, useMemo, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import { Cartridge, TextureMap } from './hashupCartridge';
import { GLTF } from 'three-stdlib';
import * as THREE from 'three';
import { Mesh, MeshStandardMaterial, Vector2 } from 'three';

interface IModel {
	cartridge: Cartridge;
	coverTextureUri: string;
}

/**
 * TODO: Change texture assets dynamically
 * @param cartridge CartridgeCarousel type to be rendered
 * @param bannerTextureUri 1112px x 720px cartridge cover image
 * @constructor
 */
export default function HashupCartridgeModel({ cartridge, coverTextureUri }: IModel) {
	const [model, setModel] = useState<GLTF>();

	let cartridgeCoverMap: THREE.Texture | null = null;

	const textureLoader = useMemo(() => new THREE.TextureLoader(), []);

	const models: { [p: number]: GLTF } = {
		[Cartridge.GOLD]: useGLTF('/assets/models/zloty/cartridge zloty.gltf'),
		[Cartridge.CUSTOM]: useGLTF('/assets/models/teczowy/cartridge teczowy.gltf'),
		[Cartridge.SILVER]: useGLTF('/assets/models/szary/cartridge szary.gltf'),
		[Cartridge._]: useGLTF('/assets/models/pusty/cartridge pusty.gltf'),
	};

	useEffect(() => {
		setModel(models[cartridge]);
	}, [cartridge]);

	/** Load whilst catching texture asset errors */
	useEffect(() => {
		try {
			cartridgeCoverMap = textureLoader.load(coverTextureUri, map => {
				/** Cover-fit the texture */
				const mapRatio = map.source.data.width / map.source.data.height;
				const meshRatio = 1.33 / 0.75;
				map.repeat.y = mapRatio / meshRatio;

				return map;
			});
		} catch {
			cartridgeCoverMap = textureLoader.load(TextureMap.FALLBACK);
		}

		return () => cartridgeCoverMap?.dispose();
	}, [model, textureLoader, coverTextureUri]);

	useEffect(() => {
		if (!model) {
			return;
		}

		model.scene.traverse(node => {
			if (node.type !== 'Mesh') {
				return;
			}

			const nodeMaterial = (node as Mesh).material as MeshStandardMaterial;

			if (nodeMaterial.name === 'wyswietlacz' && cartridgeCoverMap) {
				cartridgeCoverMap.center = new Vector2(1 / 2, 1 / 2);
				cartridgeCoverMap.rotation = Math.PI;
				cartridgeCoverMap.wrapS = THREE.RepeatWrapping;
				cartridgeCoverMap.repeat.x = -1;

				nodeMaterial.color.setHSL(0, 1, 0);
				nodeMaterial.toneMapped = false;

				nodeMaterial.map = cartridgeCoverMap;
				nodeMaterial.emissiveMap = cartridgeCoverMap;
			}
		});
	}, [model, cartridgeCoverMap, coverTextureUri]);

	return model ? <primitive object={model.scene} /> : null;
}
